import { pickT } from "@repo/shared";
import type { QuizQuestion, QuizSettings } from "@repo/shared/types";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QuizQuestionDialog } from "./quiz-question-dialog";
import { T } from "./quiz-settings-editor.i18n";

/** A brand-new quiz component: no questions, one drawn, one point to pass. */
export const EMPTY_QUIZ_SETTINGS: QuizSettings = {
  questions: [],
  minScore: 1,
  questionsToAsk: 1,
  maxAttempts: null,
  attemptResetDays: null,
};

type QuizSettingsEditorProps = {
  value: QuizSettings;
  onChange: (next: QuizSettings) => void;
  language: string;
};

/**
 * QL-13 — the quiz is authored here, inside the component that runs it. There
 * is no separate quiz entity to pick from: the component's id IS the quiz id
 * for the runtime blob, the conditional-rule source and the PDF result field.
 *
 * Shared by the add dialog (step 2) and the settings dialog, so a quiz is set
 * up the same way whether it is being created or edited.
 */
export function QuizSettingsEditor({ value, onChange, language }: QuizSettingsEditorProps) {
  const t = pickT(T, language);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<QuizQuestion | null>(null);

  const patch = (next: Partial<QuizSettings>) => onChange({ ...value, ...next });

  // QL-253 — whether any question uses a real category (drives the "per category" label).
  const hasCategories = value.questions.some((q) => q.category != null);

  const handleSaveQuestion = (question: QuizQuestion) => {
    const exists = value.questions.some((q) => q.id === question.id);
    const questions = exists
      ? value.questions.map((q) => (q.id === question.id ? question : q))
      : [...value.questions, question];
    // QL-253 — questionsToAsk no longer has an upper bound tied to the pool
    // size (a category, or the whole pool with none in use, smaller than it
    // already degrades gracefully in the draw), so growing/shrinking the bank
    // only needs to keep it at or above the floor of 1.
    patch({ questions, questionsToAsk: Math.max(value.questionsToAsk, 1) });
    setDialogOpen(false);
    setEditing(null);
  };

  const handleDeleteQuestion = (id: string) => {
    const questions = value.questions.filter((q) => q.id !== id);
    patch({ questions, questionsToAsk: Math.max(value.questionsToAsk, 1) });
  };

  /** Blank clears the limit rather than storing 0, which the server rejects. */
  const numberOrNull = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (trimmed === "") return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>
            {t.questions} <span className="text-destructive">*</span>
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t.addQuestion}
          </Button>
        </div>

        {value.questions.length === 0 ? (
          <div className="text-sm text-muted-foreground p-3 border rounded-md">{t.noQuestions}</div>
        ) : (
          <div className="space-y-2">
            {value.questions.map((question) => (
              <div
                key={question.id}
                className="flex items-center gap-2 p-3 border rounded-md bg-muted/30"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{question.label}</p>
                    {question.category != null && (
                      <span className="shrink-0 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                        {`${t.category} ${question.category}`}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {question.options.length} {t.optionCount} · {t.maxScore}{" "}
                    {Math.max(...question.options.map((o) => o.score))}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  tooltip={t.editQuestion}
                  onClick={() => {
                    setEditing(question);
                    setDialogOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  tooltip={t.delete}
                  onClick={() => handleDeleteQuestion(question.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label>{t.rules}</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="quiz-questions-to-ask" className="text-xs font-normal">
              {hasCategories ? t.questionsToAskPerCategory : t.questionsToAsk}
            </Label>
            <Input
              id="quiz-questions-to-ask"
              type="number"
              min={1}
              value={value.questionsToAsk}
              onChange={(e) => patch({ questionsToAsk: Number(e.target.value) || 1 })}
            />
            <p className="text-xs text-muted-foreground">
              {hasCategories ? t.questionsToAskPerCategoryHelp : t.questionsToAskHelp}
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="quiz-min-score" className="text-xs font-normal">
              {t.minScore}
            </Label>
            <Input
              id="quiz-min-score"
              type="number"
              min={0}
              value={value.minScore}
              onChange={(e) => patch({ minScore: Number(e.target.value) || 0 })}
            />
            <p className="text-xs text-muted-foreground">{t.minScoreHelp}</p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="quiz-max-attempts" className="text-xs font-normal">
              {t.maxAttempts}
            </Label>
            <Input
              id="quiz-max-attempts"
              type="number"
              min={1}
              placeholder={t.unlimited}
              value={value.maxAttempts ?? ""}
              onChange={(e) => patch({ maxAttempts: numberOrNull(e.target.value) })}
            />
            <p className="text-xs text-muted-foreground">{t.maxAttemptsHelp}</p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="quiz-attempt-reset-days" className="text-xs font-normal">
              {t.attemptResetDays}
            </Label>
            <Input
              id="quiz-attempt-reset-days"
              type="number"
              min={1}
              placeholder={t.never}
              value={value.attemptResetDays ?? ""}
              onChange={(e) => patch({ attemptResetDays: numberOrNull(e.target.value) })}
            />
            <p className="text-xs text-muted-foreground">{t.attemptResetDaysHelp}</p>
          </div>
        </div>
      </div>

      <QuizQuestionDialog
        open={dialogOpen}
        question={editing}
        language={language}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSaveQuestion}
      />
    </div>
  );
}
